import { useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { DescriptionOutlined } from '@material-ui/icons'
import { useDispatch, useSelector } from 'react-redux'

import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Input, Select, Textarea } from '@baseV2'
import { VEHICLE_DELAY_STATUSES, strings } from '@constants'
import { bookInvoice, uploadVehicleDocuments } from '@data/conn/post/dispatchPlan'
import { updateVehicleDelayStatus } from '@data/conn/put/dispatchPlan'
import { showLoader, showToast } from '@data/state/action'
import { toggleFileViewer } from '@data/state/action/root'
import { DrawerTitleSlotContext } from '@global/Drawer/context'
import DocumentCard from '@pages/Admin/Orders/ManageOrder/EditInvoice/InvoiceDetails/Documents/DocumentCard'
import DOCUMENT_CONFIGS from '@pages/Admin/Orders/ManageOrder/EditInvoice/InvoiceDetails/Documents/documentConfig'
import { CardsWrapper } from '@pages/Admin/Orders/ManageOrder/EditInvoice/InvoiceDetails/Documents/style'
import { SectionHeading } from '@pages/Admin/Orders/ManageOrder/Shipment/styles'

import { RemarksCard, SubmitRow } from './style'

// the cards the upload api takes, as the parts of its request: invoice, e-way bill, lorry receipt,
// test certificates, weighment slip and other documents
const uploadParts = {
	invoice: 'invoiceDocument',
	ewaybill: 'ewayBillDocument',
	bilti: 'biltyDocument',
	tc: 'tcDocuments',
	weighment_slip: 'weighmentSlip',
	other_documents: 'otherDocuments',
}
// the test certificates and the other documents take several files at once
const multipleTypes = ['tc', 'other_documents']
// not on an invoice's Documents tab, so they aren't in DOCUMENT_CONFIGS
const extraDocumentConfigs = [
	{ typeKey: 'weighment_slip', field: 'weighment_slip', isArray: false, titleKey: 'weighment_slip' },
	{ typeKey: 'other_documents', field: 'other_documents', isArray: true, titleKey: 'other_documents' },
]
// an invoice is one file per upload, but the Upload button stays after one is there so another can be added
const documentConfigs = [...DOCUMENT_CONFIGS, ...extraDocumentConfigs]
	.filter(({ typeKey }) => uploadParts[typeKey])
	.map((config) => (config.typeKey === 'invoice' ? { ...config, hideUploadWhenPresent: false } : config))

// opened from the folder button of a vehicle on Edit Dispatch Plan. The same document cards as an
// invoice's Documents tab, where picking a file uploads it straight away. The invoice number and remarks
// are submitted on their own with the button under them.
const UpdateVehicleDocsDrawer = ({ dispatchPlanId, vehicle: initialVehicle, update, order, originalSupplierPoNumber }) => {
	const dispatch = useDispatch()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const titleSlot = useContext(DrawerTitleSlotContext)
	// the drawer stays open after an upload or update, so the vehicle is refreshed from the reloaded plan
	const [vehicle, setVehicle] = useState(initialVehicle)
	// what is already saved on the vehicle shows up in the fields
	const [originalSupplierInvoice, setOriginalSupplierInvoice] = useState(vehicle?.original_supplier_invoice ?? '')
	const [remarks, setRemarks] = useState(vehicle?.remarks ?? '')
	// a vehicle that is ready for invoicing is read only
	const locked = !!vehicle?.ready_for_invoicing
	// the delay status preselected is the latest of the vehicle's logs
	const delayLogs = vehicle?.delay_status_logs ?? []
	const savedDelayStatus = delayLogs[delayLogs.length - 1]?.status ?? null
	const [delayStatus, setDelayStatus] = useState(savedDelayStatus)

	const uploadDocuments = (files, extras = {}) => {
		dispatch(showLoader(true))
		uploadVehicleDocuments(dispatchPlanId, vehicle?.vehicle_id, files, extras, loggedInUser?.user_id)
			.then(() => {
				dispatch(showToast(true, strings('msg_vehicle_documents_uploaded_success'), { type: TOAST_TYPE.SUCCESS }))
				return Promise.resolve(update?.()).then((plan) => {
					const updated = plan?.vehicle_details?.find((_vehicle) => _vehicle?.vehicle_id === vehicle?.vehicle_id)
					if (updated) setVehicle(updated)
				})
			})
			.catch((error) => {
				const message = error?.response?.data?.message ?? strings('msg_vehicle_documents_uploaded_failure')
				dispatch(showToast(true, message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	// a card sends only its own files
	const uploadHandler = (typeKey) => (files) => uploadDocuments({ [uploadParts[typeKey]]: multipleTypes.includes(typeKey) ? files : files?.[0] })

	// the button under the remarks sends only the invoice number and the remarks, and stays disabled
	// until one of them differs from what is saved
	const isEdited = originalSupplierInvoice !== (vehicle?.original_supplier_invoice ?? '') || remarks !== (vehicle?.remarks ?? '')
	const canSubmit = !locked && isEdited && (!!originalSupplierInvoice.trim() || !!remarks.trim())
	const submitHandler = () => {
		if (!canSubmit) return
		uploadDocuments({}, { originalSupplierInvoice: originalSupplierInvoice.trim() || undefined, remarks: remarks.trim() || undefined })
	}

	const canUpdateDelayStatus = !!delayStatus && delayStatus !== savedDelayStatus
	const updateDelayStatusHandler = () => {
		if (!canUpdateDelayStatus) return

		dispatch(showLoader(true))
		updateVehicleDelayStatus(loggedInUser?.user_id, dispatchPlanId, vehicle?.vehicle_id, delayStatus)
			.then(() => {
				dispatch(showToast(true, strings('msg_vehicle_status_updated_success'), { type: TOAST_TYPE.SUCCESS }))
				return Promise.resolve(update?.()).then((plan) => {
					const updated = plan?.vehicle_details?.find((_vehicle) => _vehicle?.vehicle_id === vehicle?.vehicle_id)
					if (updated) setVehicle(updated)
				})
			})
			.catch((error) => {
				const message = error?.response?.data?.message ?? strings('msg_vehicle_status_updated_fail')
				dispatch(showToast(true, message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	// the order's buyer PO, and the supplier PO of this plan's original supplier po number
	const buyerPoDocuments = order?.buyer_list?.[0]?.buyer_po_documents ?? []
	const supplierPoDocuments =
		(order?.supplier_list ?? []).find((supplier) => supplier?.original_supplier_po_number === originalSupplierPoNumber)?.supplier_po_documents ??
		[]
	const viewDocuments = (files) => dispatch(toggleFileViewer(true, { files }))

	// the invoice can be booked once the vehicle has its original supplier invoice and its vendor bill
	const canBookInvoice = !locked && !!vehicle?.original_supplier_invoice && !!vehicle?.invoice_document
	const bookInvoiceHandler = () => {
		if (!canBookInvoice) return

		dispatch(showLoader(true))
		bookInvoice(loggedInUser?.user_id, dispatchPlanId, vehicle?.vehicle_id)
			.then(() => {
				dispatch(showToast(true, strings('msg_invoice_notified_to_operations'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				const message = error?.response?.data?.message ?? error?.message ?? strings('msg_some_error_occurred_exclaim')
				dispatch(showToast(true, message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	return (
		<>
			{titleSlot &&
				createPortal(
					<>
						<Button
							type='button'
							variant='outline'
							color='primary'
							size='small'
							iconPrefix={<DescriptionOutlined />}
							disabled={!buyerPoDocuments.length}
							onClick={() => viewDocuments(buyerPoDocuments)}
						>
							{strings('buyer_po')}
						</Button>
						<Button
							type='button'
							variant='outline'
							color='primary'
							size='small'
							iconPrefix={<DescriptionOutlined />}
							disabled={!supplierPoDocuments.length}
							onClick={() => viewDocuments(supplierPoDocuments)}
						>
							{strings('supplier_po')}
						</Button>
					</>,
					titleSlot
				)}
			<DrawerBodyWrapper>
				<CardsWrapper>
					{documentConfigs.map((config) => (
						<DocumentCard
							key={config.typeKey}
							config={config}
							documents={[vehicle?.[config.field]].flat().filter(Boolean)}
							title={config.typeKey === 'invoice' ? strings('vendor_bill') : undefined}
							uploadLabel={config.typeKey === 'invoice' ? strings('upload_vendor_bill') : undefined}
							onUpload={uploadHandler(config.typeKey)}
							editable={!locked}
							defaultOpen={false}
						/>
					))}
					<RemarksCard>
						<Input
							id='original-supplier-invoice'
							label={strings('supplier_invoice')}
							placeholder={strings('supplier_invoice')}
							value={originalSupplierInvoice}
							disabled={locked}
							onChange={(e) => setOriginalSupplierInvoice(e?.target?.value)}
						/>
						<Textarea
							id='vehicle-docs-remarks'
							label={strings('remarks')}
							placeholder={strings('remarks')}
							value={remarks}
							disabled={locked}
							onChange={(e) => setRemarks(e?.target?.value)}
						/>
						<SubmitRow>
							<Button type='button' color='primary' disabled={!canSubmit} onClick={submitHandler}>
								{strings('update')}
							</Button>
						</SubmitRow>
					</RemarksCard>
					<RemarksCard>
						<SectionHeading>{strings(['update', 'vehicle', 'status'])}</SectionHeading>
						<Select
							id='vehicle-delay-status'
							label={strings('status')}
							placeholder={strings('select', 'status')}
							data={VEHICLE_DELAY_STATUSES}
							primaryKey='key'
							displayKey='label'
							groupKey='group'
							openAbove
							value={VEHICLE_DELAY_STATUSES.find((option) => option.key === delayStatus) ?? {}}
							onChange={(option) => setDelayStatus(option?.key)}
						/>
						<SubmitRow>
							<Button type='button' color='primary' disabled={!canUpdateDelayStatus} onClick={updateDelayStatusHandler}>
								{strings('update')}
							</Button>
						</SubmitRow>
					</RemarksCard>
				</CardsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button type='button' color='primary' disabled={!canBookInvoice} onClick={bookInvoiceHandler}>
					{strings('book_invoice')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default UpdateVehicleDocsDrawer
